import {
  Autocomplete, Box, InputAdornment, Paper, TextField, useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useStylesForQuestionComponent } from '../../../common/QuestionComponent';
import { AutocompleteSx } from '../../../common/utils/AutocompleteSx';
import { PushSearchHistory, selectSearchHistory } from '../../../store/slices/contentSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { menuPaperProps } from '../../../common/StyledFormInputs';

interface ContentSearchViewProps {
  onSubmit?: (searchTerm?: string) => void;
  value?: string
}

export default function ContentSearchView(props: ContentSearchViewProps) {
  const classes = useStylesForQuestionComponent();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState(props.value || '');
  const dispatch = useAppDispatch();
  const history = useAppSelector(selectSearchHistory);
  const theme = useTheme();

  useEffect(() => {
    dispatch(PushSearchHistory(searchValue));
    setSearchValue(props.value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, dispatch]);

  // This will avoid onSubmit to be invoked during startup
  const setSearchValueProxy = (value: string) => {
    setSearchValue(value);
    if (props.onSubmit) {
      props.onSubmit(value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.target as HTMLInputElement; // Ensure type safety
      setSearchValueProxy(target?.value); // Update value on Enter key press
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    }}>
      <Autocomplete
        sx={[
          {
            display: 'flex',
            gap: theme.spacing(1),
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: theme.spacing(1),
            border: '1px solid #D5D7DA',
            boxShadow: '0px 1px 2px 0px ,rgba(10, 13, 18, 0.05))',
          },
          AutocompleteSx.hideClearButton, // assuming this is a predefined style object
        ]}
        PaperComponent={({ children }) => (
          <Paper sx={menuPaperProps}>
            {children}
          </Paper>
        )}
        disablePortal
        classes={classes}
        options={history}
        openOnFocus={false}
        clearOnBlur={false}
        handleHomeEndKeys
        freeSolo
        value={searchValue}
        onChange={(_, newValue) => setSearchValueProxy(newValue ?? '')}
        renderInput={(params) => <TextField
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
            },
          }}
          onKeyDown={handleKeyDown} // Detect Enter key press
          variant="outlined"
          placeholder={t('content.feed.searchBoxPlaceholder') || ''}
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />}
      />
    </Box>
  );
}
